<script setup>
/* eslint-disable */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import feather from 'feather-icons';
import ProjectsGrid   from '../components/projects/ProjectsGrid.vue';
import ProjectMediaCarousel from '../components/projects/ProjectMediaCarousel.vue';
import ContactDetails from '../components/contact/ContactDetails.vue';
import PROJECTS_INFO  from '../data/projects_info.js';

const canvasEl  = ref(null);
const cleanupFns = [];

const contacts = [
  { id: 1, name: 'dannyvargashb@gmail.com',     icon: 'mail'     },
  { id: 2, name: 'Brooklyn, NY',                 icon: 'map-pin'  },
  { id: 3, name: 'github.com/danny-vargas-avc',  icon: 'github'   },
  { id: 4, name: 'linkedin.com/in/danielvargas96', icon: 'linkedin' },
];

// ─── Project window ───────────────────────────────────────────────────────────
const selectedProject = ref(null);
const selectedInfo = computed(() =>
  selectedProject.value ? PROJECTS_INFO[selectedProject.value.code] : null
);
const winPos     = ref({ x: null, y: null });
const winSize    = ref({ w: 720,  h: 520  });
const showWinHint = ref(false);
let _hintTimer = null;
let _drag = false, _ds = {};
let _resize = false, _rs = {};
let _split = false, _ss = {};
const splitW = ref(220);

const windowStyle = computed(() => {
  if (window.innerWidth <= 600) return {};
  const { x, y } = winPos.value;
  const { w, h } = winSize.value;
  const size = { width: w + 'px', height: h + 'px' };
  if (x === null) return { ...size, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  return { ...size, left: x + 'px', top: y + 'px', transform: 'none' };
});

const leftPanelStyle = computed(() => {
  if (window.innerWidth <= 600) return {};
  return { flex: `0 0 ${splitW.value}px` };
});

function openProject({ project }) {
  winPos.value  = { x: null, y: null };
  winSize.value = { w: 720,  h: 520  };
  splitW.value  = 220;
  selectedProject.value = project;
  showWinHint.value = true;
  clearTimeout(_hintTimer);
  _hintTimer = setTimeout(() => { showWinHint.value = false; }, 2400);
}

function closeProject() {
  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('mouseup',   onDragEnd);
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup',   onResizeEnd);
  window.removeEventListener('mousemove', onSplitMove);
  window.removeEventListener('mouseup',   onSplitEnd);
  _drag = false; _resize = false; _split = false;
  clearTimeout(_hintTimer);
  showWinHint.value = false;
  selectedProject.value = null;
  document.body.style.overflow = '';
}

// ── Drag ──────────────────────────────────────────────────────────────────────
function startDrag(e) {
  if (e.button !== 0) return;
  e.preventDefault();
  if (winPos.value.x === null) {
    const el = e.currentTarget.closest('.proj-window');
    el.style.transition = 'none';
    const r  = el.getBoundingClientRect();
    winPos.value = { x: Math.round(r.left), y: Math.round(r.top) };
  }
  _drag = true;
  _ds   = { mx: e.clientX, my: e.clientY, wx: winPos.value.x, wy: winPos.value.y };
  window.addEventListener('mousemove', onDragMove);
  window.addEventListener('mouseup',   onDragEnd);
}

function onDragMove(e) {
  if (!_drag) return;
  const w = winSize.value.w;
  winPos.value = {
    x: Math.max(-(w - 120), Math.min(_ds.wx + e.clientX - _ds.mx, window.innerWidth  - 120)),
    y: Math.max(0,           Math.min(_ds.wy + e.clientY - _ds.my, window.innerHeight - 44)),
  };
}

function onDragEnd() {
  _drag = false;
  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('mouseup',   onDragEnd);
}

// ── Resize ────────────────────────────────────────────────────────────────────
const MIN_W = 340, MIN_H = 260;

function startResize(dir, e) {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  if (winPos.value.x === null) {
    const el = e.currentTarget.closest('.proj-window');
    el.style.transition = 'none';
    const r  = el.getBoundingClientRect();
    winPos.value  = { x: Math.round(r.left), y: Math.round(r.top) };
    winSize.value = { w: Math.round(r.width), h: Math.round(r.height) };
  }
  _resize = true;
  _rs = {
    dir,
    mx: e.clientX, my: e.clientY,
    wx: winPos.value.x,  wy: winPos.value.y,
    ww: winSize.value.w, wh: winSize.value.h,
  };
  window.addEventListener('mousemove', onResizeMove);
  window.addEventListener('mouseup',   onResizeEnd);
}

function onResizeMove(e) {
  if (!_resize) return;
  const dx  = e.clientX - _rs.mx;
  const dy  = e.clientY - _rs.my;
  const dir = _rs.dir;
  let { wx, wy, ww, wh } = _rs;

  if (dir.includes('e')) { ww = Math.max(MIN_W, ww + dx); }
  if (dir.includes('s')) { wh = Math.max(MIN_H, wh + dy); }
  if (dir.includes('w')) { const nw = Math.max(MIN_W, ww - dx); wx += ww - nw; ww = nw; }
  if (dir.includes('n')) { const nh = Math.max(MIN_H, wh - dy); wy += wh - nh; wh = nh; }

  winPos.value  = { x: wx, y: wy };
  winSize.value = { w: ww, h: wh };
}

function onResizeEnd() {
  _resize = false;
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup',   onResizeEnd);
}

// ── Panel splitter ────────────────────────────────────────────────────────────
function startSplit(e) {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  _split = true;
  _ss = { mx: e.clientX, sw: splitW.value };
  window.addEventListener('mousemove', onSplitMove);
  window.addEventListener('mouseup',   onSplitEnd);
}

function onSplitMove(e) {
  if (!_split) return;
  splitW.value = Math.max(140, Math.min(_ss.sw + (e.clientX - _ss.mx), winSize.value.w - 200));
}

function onSplitEnd() {
  _split = false;
  window.removeEventListener('mousemove', onSplitMove);
  window.removeEventListener('mouseup',   onSplitEnd);
}

const onKeydown = (e) => { if (e.key === 'Escape') closeProject(); };

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function onOverlayBeforeEnter(el) {
  el.style.transition = 'none';
  el.style.opacity    = '0';
  if (window.innerWidth <= 600) {
    el._bt = '';
    el.style.transform = 'translateY(100%)';
  } else {
    const t = el.style.transform || 'translate(-50%, -50%)';
    el._bt  = t;
    el.style.transform = t + ' scale(0.88)';
  }
}

function onOverlayEnter(el, done) {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (window.innerWidth <= 600) {
      el.style.transition = 'opacity 0.22s ease, transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      el.style.opacity    = '1';
      el.style.transform  = 'translateY(0)';
    } else {
      el.style.transition = 'opacity 0.2s ease, transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)';
      el.style.opacity    = '1';
      el.style.transform  = el._bt;
    }
    el.addEventListener('transitionend', done, { once: true });
  }));
}

function onOverlayLeave(el, done) {
  if (window.innerWidth <= 600) {
    el.style.transition = 'opacity 0.2s ease, transform 0.28s ease';
    el.style.opacity    = '0';
    el.style.transform  = 'translateY(100%)';
  } else {
    el.style.transition = 'opacity 0.18s ease, transform 0.18s ease';
    el.style.opacity    = '0';
    el.style.transform  = (el._bt || 'translate(-50%, -50%)') + ' scale(0.94)';
  }
  setTimeout(done, 300);
}

// ─── Presets & palettes (from Graze) ──────────────────────────────────────────
const PRESETS = [
  { name: 'coral',   f: 0.0545, k: 0.062,  seeds: 20 },
  { name: 'maze',    f: 0.029,  k: 0.057,  seeds: 20 },
  { name: 'bubbles', f: 0.012,  k: 0.050,  seeds: 25 },
  { name: 'waves',   f: 0.014,  k: 0.045,  seeds: 20 },
  { name: 'worms',   f: 0.040,  k: 0.060,  seeds: 20 },
];

const PALETTES = [
  { bg: [0.04, 0.04, 0.04], fg: [0.76, 0.55, 0.38] },  // charcoal + terracotta
  { bg: [0.04, 0.04, 0.04], fg: [0.55, 0.65, 0.45] },  // charcoal + olive
  { bg: [0.04, 0.04, 0.04], fg: [0.85, 0.70, 0.50] },  // charcoal + amber
  { bg: [0.04, 0.04, 0.04], fg: [0.45, 0.55, 0.50] },  // charcoal + sage
];

// ─── Shaders ──────────────────────────────────────────────────────────────────

const VERT_SRC = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }`;

const SIM_SRC = `
  precision highp float;
  uniform sampler2D u_texture;
  uniform vec2  u_resolution;
  uniform float u_feed;
  uniform float u_kill;
  uniform vec2  u_mouse;
  uniform float u_mouseActive;
  uniform float u_mouseRadius;
  uniform float u_dt;
  varying vec2 v_uv;

  void main() {
    vec2 texel = 1.0 / u_resolution;
    vec4 state = texture2D(u_texture, v_uv);
    float a = state.r;
    float b = state.g;

    float la = -a;
    float lb = -b;

    la += texture2D(u_texture, v_uv + vec2(-texel.x,  0.0     )).r * 0.2;
    la += texture2D(u_texture, v_uv + vec2( texel.x,  0.0     )).r * 0.2;
    la += texture2D(u_texture, v_uv + vec2( 0.0,     -texel.y )).r * 0.2;
    la += texture2D(u_texture, v_uv + vec2( 0.0,      texel.y )).r * 0.2;
    la += texture2D(u_texture, v_uv + vec2(-texel.x, -texel.y )).r * 0.05;
    la += texture2D(u_texture, v_uv + vec2( texel.x, -texel.y )).r * 0.05;
    la += texture2D(u_texture, v_uv + vec2(-texel.x,  texel.y )).r * 0.05;
    la += texture2D(u_texture, v_uv + vec2( texel.x,  texel.y )).r * 0.05;

    lb += texture2D(u_texture, v_uv + vec2(-texel.x,  0.0     )).g * 0.2;
    lb += texture2D(u_texture, v_uv + vec2( texel.x,  0.0     )).g * 0.2;
    lb += texture2D(u_texture, v_uv + vec2( 0.0,     -texel.y )).g * 0.2;
    lb += texture2D(u_texture, v_uv + vec2( 0.0,      texel.y )).g * 0.2;
    lb += texture2D(u_texture, v_uv + vec2(-texel.x, -texel.y )).g * 0.05;
    lb += texture2D(u_texture, v_uv + vec2( texel.x, -texel.y )).g * 0.05;
    lb += texture2D(u_texture, v_uv + vec2(-texel.x,  texel.y )).g * 0.05;
    lb += texture2D(u_texture, v_uv + vec2( texel.x,  texel.y )).g * 0.05;

    const float Da = 1.0;
    const float Db = 0.5;

    float mouseDist     = distance(v_uv, u_mouse);
    float mouseInfluence = smoothstep(u_mouseRadius, 0.0, mouseDist) * u_mouseActive;

    float abb = a * b * b;
    float da  = Da * la - abb + u_feed * (1.0 - a);
    float db  = Db * lb + abb - (u_kill + u_feed) * b;

    a = clamp(a + da * u_dt, 0.0, 1.0);
    b = clamp(b + db * u_dt + mouseInfluence * 0.12, 0.0, 1.0);

    gl_FragColor = vec4(a, b, 0.0, 1.0);
  }`;

// Embossed display: Sobel edges + fake directional light = 3-D depth
// Mouse position warps UV coordinates with a gravitational-lens falloff
const DISP_SRC = `
  precision highp float;
  uniform sampler2D u_texture;
  uniform vec2 u_resolution;
  uniform vec3 u_bgColor;
  uniform vec3 u_fgColor;
  uniform vec2 u_mouse;
  uniform vec2 u_mouseVel;
  uniform float u_mouseActive;
  varying vec2 v_uv;

  void main() {
    // Vortex: radial pull + tangential swirl around cursor
    vec2 toMouse  = u_mouse - v_uv;
    float mDist   = length(toMouse);
    float falloff = smoothstep(0.04, 0.0, mDist) * u_mouseActive;
    vec2 radial   = (toMouse / max(mDist, 0.001)) * 0.045;
    vec2 tangent  = vec2(-toMouse.y, toMouse.x) / max(mDist, 0.001) * 0.06;
    vec2 sampleUv = clamp(v_uv + (radial + tangent) * falloff, 0.0, 1.0);

    vec2 texel = 1.0 / u_resolution;

    float b  = texture2D(u_texture, sampleUv).g;
    float bL = texture2D(u_texture, sampleUv + vec2(-texel.x,  0.0    )).g;
    float bR = texture2D(u_texture, sampleUv + vec2( texel.x,  0.0    )).g;
    float bD = texture2D(u_texture, sampleUv + vec2( 0.0,     -texel.y)).g;
    float bU = texture2D(u_texture, sampleUv + vec2( 0.0,      texel.y)).g;

    float gx   = bR - bL;
    float gy   = bU - bD;
    float edge = sqrt(gx * gx + gy * gy);
    float light = (gx + gy) * 2.0;

    vec3 base      = mix(u_bgColor, u_fgColor, b);
    vec3 highlight = base + vec3(0.12) * light;
    vec3 color     = highlight + edge * vec3(0.08);

    gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
  }`;

// ─── Vue lifecycle ─────────────────────────────────────────────────────────────

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  cleanupFns.push(() => window.removeEventListener('keydown', onKeydown));

  const canvas = canvasEl.value;
  const gl = canvas.getContext('webgl', { alpha: false, antialias: false });
  if (!gl) { console.warn('WebGL not supported'); return; }

  if (!gl.getExtension('OES_texture_float')) {
    console.warn('OES_texture_float not supported'); return;
  }
  gl.getExtension('OES_texture_float_linear');

  // Pick a random preset + palette (reassignable so they can rotate each cycle)
  let preset  = PRESETS [Math.floor(Math.random() * PRESETS.length)];
  let palette = PALETTES[Math.floor(Math.random() * PALETTES.length)];

  // ── Compile helpers ────────────────────────────────────────────────────────
  function makeShader(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
      console.error(gl.getShaderInfoLog(s));
    return s;
  }
  function makeProgram(vSrc, fSrc) {
    const p = gl.createProgram();
    gl.attachShader(p, makeShader(gl.VERTEX_SHADER,   vSrc));
    gl.attachShader(p, makeShader(gl.FRAGMENT_SHADER, fSrc));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS))
      console.error(gl.getProgramInfoLog(p));
    return p;
  }

  const simProg  = makeProgram(VERT_SRC, SIM_SRC);
  const dispProg = makeProgram(VERT_SRC, DISP_SRC);

  // ── Quad buffer ────────────────────────────────────────────────────────────
  const quadBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
  gl.bufferData(gl.ARRAY_BUFFER,
    new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]),
    gl.STATIC_DRAW);

  // ── Simulation dimensions ──────────────────────────────────────────────────
  let simW, simH;

  let textures = [];
  let current = 0;

  function makeTexFbo() {
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, simW, simH, 0, gl.RGBA, gl.FLOAT, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { tex, fbo };
  }

  function seed() {
    const data = new Float32Array(simW * simH * 4);
    for (let i = 0; i < simW * simH; i++) {
      data[i*4]     = 1.0;
      data[i*4 + 3] = 1.0;
    }
    const n = preset.seeds + Math.floor(Math.random() * Math.floor(preset.seeds * 0.5));
    for (let s = 0; s < n; s++) {
      const cx = Math.floor(Math.random() * simW);
      const cy = Math.floor(Math.random() * simH);
      const r  = 8 + Math.floor(Math.random() * 20);
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const d = Math.sqrt(dx*dx + dy*dy);
          if (d >= r) continue;
          const x   = (cx + dx + simW) % simW;
          const y   = (cy + dy + simH) % simH;
          const idx = (y * simW + x) * 4;
          const f   = 1.0 - d / r;
          data[idx]     = 0.5 + 0.25 * f;
          data[idx + 1] = 0.25 + 0.75 * f;
        }
      }
    }
    for (let i = 0; i < 2; i++) {
      gl.bindTexture(gl.TEXTURE_2D, textures[i].tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, simW, simH, 0, gl.RGBA, gl.FLOAT, data);
    }
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = Math.floor(window.innerWidth  * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    simW = Math.floor(canvas.width  * 0.5);
    simH = Math.floor(canvas.height * 0.5);

    textures.forEach(t => { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fbo); });
    textures = [makeTexFbo(), makeTexFbo()];
    seed();
  }
  resize();
  window.addEventListener('resize', resize);
  cleanupFns.push(() => window.removeEventListener('resize', resize));

  // ── Mouse / touch ──────────────────────────────────────────────────────────
  let mx = 0.5, my = 0.5, mActive = false;
  let mvx = 0, mvy = 0;

  const isMobile = 'ontouchstart' in window || window.innerWidth < 768;
  const mRadius  = isMobile ? 0.08 : 0.035;

  function onMove(cx, cy) {
    const nx = cx / window.innerWidth;
    const ny = 1.0 - cy / window.innerHeight;
    mvx = mvx * 0.6 + (nx - mx) * 3;
    mvy = mvy * 0.6 + (ny - my) * 3;
    mx = nx; my = ny;
  }
  const onMouseMove  = e => { onMove(e.clientX, e.clientY); mActive = true; };
  const onMouseLeave = () => { mActive = false; };
  const onTouchMove  = e => { e.preventDefault(); onMove(e.touches[0].clientX, e.touches[0].clientY); mActive = true; };
  const onTouchEnd   = () => { mActive = false; };

  window.addEventListener('mousemove',  onMouseMove);
  window.addEventListener('mouseleave', onMouseLeave);
  canvas.addEventListener('touchmove',  onTouchMove, { passive: false });
  canvas.addEventListener('touchend',   onTouchEnd);
  cleanupFns.push(() => {
    window.removeEventListener('mousemove',  onMouseMove);
    window.removeEventListener('mouseleave', onMouseLeave);
    canvas.removeEventListener('touchmove',  onTouchMove);
    canvas.removeEventListener('touchend',   onTouchEnd);
  });

  // ── Simulation step ────────────────────────────────────────────────────────
  function simStep() {
    const next = 1 - current;
    gl.useProgram(simProg);
    gl.bindFramebuffer(gl.FRAMEBUFFER, textures[next].fbo);
    gl.viewport(0, 0, simW, simH);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, textures[current].tex);
    gl.uniform1i(gl.getUniformLocation(simProg, 'u_texture'),     0);
    gl.uniform2f(gl.getUniformLocation(simProg, 'u_resolution'),  simW, simH);
    gl.uniform1f(gl.getUniformLocation(simProg, 'u_feed'),        simF);
    gl.uniform1f(gl.getUniformLocation(simProg, 'u_kill'),        simK);
    gl.uniform2f(gl.getUniformLocation(simProg, 'u_mouse'),       mx, my);
    gl.uniform1f(gl.getUniformLocation(simProg, 'u_mouseActive'), mActive ? 1.0 : 0.0);
    gl.uniform1f(gl.getUniformLocation(simProg, 'u_mouseRadius'), mRadius);
    gl.uniform1f(gl.getUniformLocation(simProg, 'u_dt'),          1.0);

    const loc = gl.getAttribLocation(simProg, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    current = next;
  }

  // ── Display ────────────────────────────────────────────────────────────────
  function display() {
    gl.useProgram(dispProg);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, textures[current].tex);
    gl.uniform1i(gl.getUniformLocation(dispProg, 'u_texture'),     0);
    gl.uniform2f(gl.getUniformLocation(dispProg, 'u_resolution'),  simW, simH);
    const t  = paletteLerp * paletteLerp * (3 - 2 * paletteLerp); // smoothstep
    const bg = paletteFrom.bg.map((v, i) => v + (paletteTo.bg[i] - v) * t);
    const fg = paletteFrom.fg.map((v, i) => v + (paletteTo.fg[i] - v) * t);
    gl.uniform3f(gl.getUniformLocation(dispProg, 'u_bgColor'),     ...bg);
    gl.uniform3f(gl.getUniformLocation(dispProg, 'u_fgColor'),     ...fg);
    gl.uniform2f(gl.getUniformLocation(dispProg, 'u_mouse'),       mx, my);
    gl.uniform1f(gl.getUniformLocation(dispProg, 'u_mouseActive'), mActive ? 1.0 : 0.0);
    gl.uniform2f(gl.getUniformLocation(dispProg, 'u_mouseVel'),    mvx, mvy);

    const loc = gl.getAttribLocation(dispProg, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // ── Periodic re-seeding ────────────────────────────────────────────────────
  let frameCount = 0;

  function plantSeed() {
    const r   = 8 + Math.floor(Math.random() * 12);
    const sz  = r * 2;
    const cx  = r + Math.floor(Math.random() * (simW - sz));
    const cy  = r + Math.floor(Math.random() * (simH - sz));
    const p   = new Float32Array(sz * sz * 4);
    for (let py = 0; py < sz; py++) {
      for (let px = 0; px < sz; px++) {
        const d = Math.sqrt((px-r)*(px-r) + (py-r)*(py-r));
        const i = (py * sz + px) * 4;
        if (d < r) {
          const f = 1.0 - d/r;
          p[i]     = 0.5 + 0.25*f;
          p[i + 1] = 0.25 + 0.75*f;
          p[i + 3] = 1.0;
        } else {
          p[i] = 1.0; p[i+3] = 1.0;
        }
      }
    }
    gl.bindTexture(gl.TEXTURE_2D, textures[current].tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, cx, cy, sz, sz, gl.RGBA, gl.FLOAT, p);
  }

  // ── Phase cycle: grow → erase → grow … ────────────────────────────────────
  let simF = preset.f;
  let simK = preset.k;
  let phase      = 'grow';
  let phaseFrame = 0;
  const GROW_FRAMES  = 900;
  const ERASE_FRAMES = 1600;

  // ── Palette crossfade ──────────────────────────────────────────────────────
  let paletteFrom  = palette;
  let paletteTo    = palette;
  let paletteLerp  = 1.0;
  const FADE_FRAMES = 600; // completes well before erase finishes

  // ── Render loop ────────────────────────────────────────────────────────────
  let rafId = null;
  function frame() {
    rafId = requestAnimationFrame(frame);
    frameCount++;
    phaseFrame++;

    if (paletteLerp < 1.0) paletteLerp = Math.min(1.0, paletteLerp + 1 / FADE_FRAMES);

    if (phase === 'grow') {
      if (frameCount % 200 === 0) plantSeed();
      if (phaseFrame >= GROW_FRAMES) {
        phase = 'erase';
        phaseFrame = 0;
        simF = preset.f * 0.92;
        simK = preset.k + 0.001;
        // Kick off palette + preset crossfade — completes well before erase ends
        let nextPreset = preset;
        while (nextPreset === preset) nextPreset = PRESETS[Math.floor(Math.random() * PRESETS.length)];
        preset      = nextPreset;
        paletteFrom = paletteTo;
        paletteTo   = PALETTES[Math.floor(Math.random() * PALETTES.length)];
        paletteLerp = 0.0;
      }
    } else {
      if (phaseFrame >= ERASE_FRAMES) {
        phase = 'grow';
        phaseFrame = 0;
        simF = preset.f;
        simK = preset.k;
        seed();
        for (let s = 0; s < 12; s++) plantSeed();
      }
    }

    mvx *= 0.88; mvy *= 0.88;
    const stepsPerFrame = isMobile ? 4 : 8;
    for (let i = 0; i < stepsPerFrame; i++) simStep();
    display();
  }

  rafId = requestAnimationFrame(frame);
  cleanupFns.push(() => cancelAnimationFrame(rafId));

  feather.replace();
});

onUnmounted(() => {
  window.removeEventListener('mousemove', onDragMove);
  window.removeEventListener('mouseup',   onDragEnd);
  window.removeEventListener('mousemove', onResizeMove);
  window.removeEventListener('mouseup',   onResizeEnd);
  window.removeEventListener('mousemove', onSplitMove);
  window.removeEventListener('mouseup',   onSplitEnd);
  cleanupFns.forEach(fn => fn());
  cleanupFns.length = 0;
});
</script>

<template>
  <div>
    <!-- ── Hero ───────────────────────────────────────────────────────────── -->
    <section class="hero">
      <canvas ref="canvasEl" class="rd-canvas"></canvas>

      <div class="hero-id">
        <h1 class="hero-name">DANIEL VARGAS</h1>
        <p class="hero-sub">FULL-STACK DEVELOPER · AV&amp;C · BROOKLYN NY</p>
        <p class="hero-bio">Building software for physical spaces.</p>
        <div class="hero-stack">
          <span class="hero-stack-tag">Vue.js</span>
          <span class="hero-stack-tag">WebGL</span>
          <span class="hero-stack-tag">Three.js</span>
          <span class="hero-stack-tag">Python</span>
          <span class="hero-stack-tag">Node.js</span>
        </div>
      </div>

      <nav class="hero-nav">
        <a @click="scrollTo('work')"    class="nav-link">Work</a>
        <a @click="scrollTo('contact')" class="nav-link">Contact</a>
      </nav>

      <p class="drag-hint">move cursor to paint</p>
    </section>

    <!-- ── Work ──────────────────────────────────────────────────────────── -->
    <section id="work" class="content-section">
      <div class="inner">
        <h2 class="section-label">WORK</h2>
        <ProjectsGrid @select="openProject" />
      </div>
    </section>

    <!-- ── Contact ───────────────────────────────────────────────────────── -->
    <footer id="contact" class="contact-footer">
      <div class="contact-inner">
        <p class="contact-eyebrow">Get in touch</p>
        <a href="mailto:dannyvargashb@gmail.com" class="contact-email">
          Let's build something.
        </a>

        <div class="contact-divider"></div>

        <div class="contact-row">
          <div class="contact-links">
            <a href="https://github.com/danny-vargas-avc" target="_blank" rel="noopener" class="contact-link">GitHub</a>
            <a href="https://www.linkedin.com/in/danielvargas96" target="_blank" rel="noopener" class="contact-link">LinkedIn</a>
            <span class="contact-link contact-location">Brooklyn, NY</span>
          </div>
          <p class="contact-copy">&copy; {{ new Date().getFullYear() }} Daniel Vargas</p>
        </div>
      </div>
    </footer>

    <!-- ── Project window ───────────────────────────────────────────────── -->
    <Transition name="bdfade">
      <div v-if="selectedProject" class="proj-backdrop" @click="closeProject" />
    </Transition>

    <Transition
      :css="false"
      @before-enter="onOverlayBeforeEnter"
      @enter="onOverlayEnter"
      @leave="onOverlayLeave"
    >
      <div v-if="selectedProject && selectedInfo" class="proj-window" :style="windowStyle">
        <!-- Resize handles -->
        <div class="rh rh-n"  @mousedown.stop="startResize('n',  $event)"></div>
        <div class="rh rh-ne" @mousedown.stop="startResize('ne', $event)"></div>
        <div class="rh rh-e"  @mousedown.stop="startResize('e',  $event)"></div>
        <div class="rh rh-se" @mousedown.stop="startResize('se', $event)"></div>
        <div class="rh rh-s"  @mousedown.stop="startResize('s',  $event)"></div>
        <div class="rh rh-sw" @mousedown.stop="startResize('sw', $event)"></div>
        <div class="rh rh-w"  @mousedown.stop="startResize('w',  $event)"></div>
        <div class="rh rh-nw" @mousedown.stop="startResize('nw', $event)"></div>

        <!-- Title bar / drag handle -->
        <div class="proj-titlebar" @mousedown="startDrag">
          <div class="proj-titlebar-meta">
            <span class="proj-titlebar-title">{{ selectedInfo.singleProjectHeader.singleProjectTitle }}</span>
            <span class="proj-titlebar-tag">
              {{ selectedInfo.singleProjectHeader.singleProjectDate }}
              &nbsp;·&nbsp;
              {{ selectedInfo.singleProjectHeader.singleProjectTag }}
            </span>
          </div>
          <Transition name="hint-fade">
            <span v-if="showWinHint" class="proj-win-hint">drag · resize</span>
          </Transition>
          <button class="proj-close" @click.stop="closeProject" aria-label="Close">&#x2715;</button>
        </div>

        <!-- Scrollable body -->
        <div class="proj-win-body">
          <div class="proj-win-left" :style="leftPanelStyle">
            <div class="proj-block">
              <p class="proj-block-heading">{{ selectedInfo.projectInfo.clientHeading }}</p>
              <ul>
                <li v-for="info in selectedInfo.projectInfo.companyInfos" :key="info.id" class="proj-info-row">
                  <span class="proj-info-label">{{ info.title }}</span>
                  <span class="proj-info-value">{{ info.details }}</span>
                </li>
              </ul>
            </div>
            <div class="proj-block">
              <p class="proj-block-heading">{{ selectedInfo.projectInfo.objectivesHeading }}</p>
              <p class="proj-body-text">{{ selectedInfo.projectInfo.objectivesDetails }}</p>
            </div>
            <div class="proj-block">
              <p class="proj-block-heading">{{ selectedInfo.projectInfo.technologies[0].title }}</p>
              <p class="proj-body-text">{{ selectedInfo.projectInfo.technologies[0].techs.join(' · ') }}</p>
            </div>
          </div>

          <div class="proj-splitter" @mousedown="startSplit"></div>

          <div class="proj-win-right">
            <ProjectMediaCarousel :projectImages="selectedInfo.projectImages" />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.hero {
  position: relative;
  width: 100%;
  height: 100vh;
  z-index: 1;
}
.rd-canvas {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  z-index: 0;
}
.hero-id {
  position: absolute;
  bottom: 36px;
  left: 40px;
  pointer-events: none;
}
.hero-name {
  font-family: 'Library-3AM', 'Helvetica Neue', Arial, sans-serif;
  font-size: clamp(1.6rem, 4vw, 3.6rem);
  color: rgba(255,255,255,0.88);
  line-height: 1;
  margin: 0 0 6px;
  letter-spacing: 0.04em;
}
.hero-sub {
  color: rgba(255,255,255,0.32);
  font-size: clamp(0.6rem, 1.1vw, 0.78rem);
  font-family: 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: 0.2em;
  margin: 0 0 12px;
}
.hero-bio {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: clamp(0.7rem, 1.1vw, 0.82rem);
  color: rgba(255,255,255,0.38);
  margin: 0 0 12px;
  letter-spacing: 0.04em;
}
.hero-stack {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.hero-stack-tag {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 2px;
  padding: 3px 8px;
}
.hero-nav {
  position: absolute;
  bottom: 40px;
  right: 40px;
  display: flex;
  gap: 28px;
}
.nav-link {
  color: rgba(255,255,255,0.35);
  font-size: 0.78rem;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s;
}
.nav-link:hover { color: rgba(255,255,255,0.82); }
.drag-hint {
  position: absolute;
  top: 28px;
  right: 36px;
  color: rgba(255,255,255,0.18);
  font-size: 0.68rem;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: 0.18em;
  pointer-events: none;
}
.content-section {
  position: relative;
  z-index: 1;
  background: rgba(10, 10, 10, 0.785);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 80px 0 100px;
  min-height: 60vh;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}
.inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 32px;
}
.section-label {
  font-family: 'Library-3AM', 'Helvetica Neue', Arial, sans-serif;
  font-size: clamp(1.2rem, 2.5vw, 2.2rem);
  color: #8f8f8f;
  margin: 0 0 32px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,99,25,0.4);
  display: inline-block;
}

/* ── Project floating window ───────────────────────────────────────────────── */
.proj-backdrop {
  position: fixed;
  inset: 0;
  z-index: 199;
  background: rgba(0, 0, 0, 0.45);
}
.bdfade-enter-active, .bdfade-leave-active { transition: opacity 0.2s ease; }
.bdfade-enter-from,   .bdfade-leave-to     { opacity: 0; }

.proj-window {
  position: fixed;
  z-index: 200;
  width: 720px;
  height: 520px;
  min-width: 340px;
  min-height: 260px;
  max-width: 92vw;
  max-height: 88vh;
  background: rgba(12, 12, 12, 0.92);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 40px 100px rgba(0,0,0,0.7), 0 0 0 0.5px rgba(255,255,255,0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rh { position: absolute; z-index: 10; }
.rh-n  { top: 0;    left: 8px;  right: 8px;  height: 5px; cursor: n-resize;  }
.rh-s  { bottom: 0; left: 8px;  right: 8px;  height: 5px; cursor: s-resize;  }
.rh-e  { right: 0;  top: 8px;   bottom: 8px; width:  5px; cursor: e-resize;  }
.rh-w  { left: 0;   top: 8px;   bottom: 8px; width:  5px; cursor: w-resize;  }
.rh-nw { top: 0;    left: 0;    width: 12px; height: 12px; cursor: nw-resize; }
.rh-ne { top: 0;    right: 0;   width: 12px; height: 12px; cursor: ne-resize; }
.rh-sw { bottom: 0; left: 0;    width: 12px; height: 12px; cursor: sw-resize; }
.rh-se { bottom: 0; right: 0;   width: 12px; height: 12px; cursor: se-resize; }
.proj-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px 13px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  cursor: grab;
  flex: 0 0 auto;
  user-select: none;
  -webkit-user-select: none;
}
.proj-titlebar:active { cursor: grabbing; }
.proj-titlebar-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.proj-titlebar-title {
  font-family: 'Library-3AM', 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.82);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.proj-titlebar-tag {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.22);
}
.proj-close {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.45);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 26px;
  transition: border-color 0.15s, color 0.15s;
}
.proj-close:hover { border-color: rgba(255,255,255,0.4); color: #fff; }
.proj-win-hint {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.2);
  white-space: nowrap;
}
.hint-fade-enter-active { transition: opacity 0.4s ease; }
.hint-fade-leave-active { transition: opacity 0.8s ease; }
.hint-fade-enter-from, .hint-fade-leave-to { opacity: 0; }
.proj-win-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.proj-win-left {
  overflow-y: auto;
  padding: 20px 18px;
  overscroll-behavior: contain;
  scrollbar-width: none;
  min-width: 0;
}
.proj-win-left::-webkit-scrollbar { display: none; }
.proj-splitter {
  flex: 0 0 5px;
  cursor: col-resize;
  background: rgba(255, 255, 255, 0.06);
  transition: background 0.15s;
}
.proj-splitter:hover { background: rgba(255, 255, 255, 0.14); }
.proj-win-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 16px 12px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}
.proj-block { margin-bottom: 28px; }
.proj-block-heading {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  margin: 0 0 10px;
}
.proj-info-row {
  display: flex;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 0.8rem;
  line-height: 1.5;
  list-style: none;
}
.proj-info-label { color: rgba(255,255,255,0.3);  flex: 0 0 68px; font-family: 'Helvetica Neue', Arial, sans-serif; }
.proj-info-value { color: rgba(255,255,255,0.68); font-family: 'Helvetica Neue', Arial, sans-serif; }
.proj-body-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  line-height: 1.7;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  margin: 0;
}

@media (max-width: 600px) {
  /* hero */
  .hero-id  { bottom: 20px; left: 20px; right: 20px; }
  .hero-nav { bottom: 20px; right: 20px; gap: 20px; }
  .drag-hint { display: none; }
  .inner { padding: 0 20px; }
  .content-section { padding: 60px 0 80px; }

  /* project window — fullscreen slide-up sheet */
  .proj-window {
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    resize: none;
  }
  .rh           { display: none; }
  .proj-splitter { display: none; }
  .proj-win-hint { display: none; }
  .proj-titlebar { cursor: default; }
  .proj-win-body { flex-direction: column; }
  .proj-win-left { flex: 0 0 auto !important; border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); max-height: 45%; }
}

/* ── Contact footer ────────────────────────────────────────────────────────── */
.contact-footer {
  position: relative;
  z-index: 1;
  background: rgba(10, 10, 10, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  padding: 100px 0 56px;
}
.contact-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 40px;
}
.contact-eyebrow {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.28);
  margin: 0 0 20px;
}
.contact-email {
  display: block;
  font-family: 'Library-3AM', 'Helvetica Neue', Arial, sans-serif;
  font-size: clamp(0.9rem, 4.2vw, 4rem);
  color: rgba(255, 255, 255, 0.82);
  letter-spacing: -0.01em;
  line-height: 1;
  text-decoration: none;
  transition: color 0.2s;
  white-space: nowrap;
}
.contact-email:hover {
  color: #fff;
}
.contact-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
  margin: 56px 0 28px;
}
.contact-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.contact-links {
  display: flex;
  gap: 32px;
  align-items: center;
}
.contact-link {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.38);
  text-decoration: none;
  transition: color 0.2s;
}
a.contact-link:hover {
  color: rgba(255, 255, 255, 0.8);
}
.contact-location {
  cursor: default;
}
.contact-copy {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.18);
  margin: 0;
}

@media (max-width: 600px) {
  .contact-footer { padding: 72px 0 48px; }
  .contact-inner  { padding: 0 24px; }
  .contact-row    { flex-direction: column; align-items: flex-start; gap: 16px; }
}
</style>
